import React, { useMemo } from "react";
import { Accordion, Badge, Form } from "react-bootstrap";

function isToggleItem(it) {
  return it?.type !== "accessLevel";
}

function getEnabledCount(items, values) {
  let enabled = 0;
  for (const it of items) {
    if (!isToggleItem(it)) continue;
    if (values?.[it.key]) enabled += 1;
  }
  return enabled;
}

function getToggleTotal(items) {
  return (items || []).filter(isToggleItem).length;
}

const FeatureConfiguration = ({
  title = "Feature Configuration",
  helperText = "Enable the modules that should be available for this entity.",
  sections = [],
  values = {},
  onToggle,
  onAccessLevelChange,
}) => {
  const computed = useMemo(() => {
    return sections.map((s) => {
      const total = getToggleTotal(s.items);
      const enabled = total ? getEnabledCount(s.items, values) : 0;
      return { ...s, total, enabled };
    });
  }, [sections, values]);

  return (
    <div className="feature-config">
      <div className="feature-config__title-row">
        <div>
          <div className="feature-config__title">{title}</div>
        </div>
        <div className="feature-config__helper">{helperText}</div>
      </div>

      <Accordion className="feature-config__accordion" alwaysOpen>
        {computed.map((section) => (
          <Accordion.Item
            key={section.id}
            eventKey={String(section.id)}
            className={`feature-config__item feature-config__item--${String(section.id)}`}
          >
            <Accordion.Header>
              <div className="feature-config__header">
                <div className="feature-config__header-left">
                  <div className="feature-config__section-title">
                    {section.title}
                  </div>
                  {section.description ? (
                    <div className="feature-config__section-desc">
                      {section.description}
                    </div>
                  ) : null}
                </div>
                <div className="feature-config__header-right">
                  <Badge pill bg="primary" className="feature-config__badge">
                    {section.enabled}/{section.total} Enabled
                  </Badge>
                </div>
              </div>
            </Accordion.Header>

            <Accordion.Body className="feature-config__body">
              {(section.items || []).map((it) => {
                if (it.type === "accessLevel") {
                  const accessLevelValue =
                    values?.[it.key] ?? it.defaultValue ?? "ALL";

                  return (
                    <div key={it.key} className="feature-config__subcard">
                      <div className="feature-config__access-level feature-config__access-level--standalone">
                        <div className="feature-config__access-level-label">
                          {it.label || "DEFINE DEVICE USER ACCESS LEVEL"}
                        </div>
                        {it.helper ? (
                          <div className="feature-config__toggle-helper mb-2">
                            {it.helper}
                          </div>
                        ) : null}
                        <div className="feature-config__access-level-options">
                          {(it.options || []).map((opt) => (
                            <Form.Check
                              key={opt.value}
                              inline
                              type="radio"
                              id={`feature-${section.id}-${it.key}-${opt.value}`}
                              name={`feature-access-${section.id}-${it.key}`}
                              label={opt.label}
                              value={opt.value}
                              checked={accessLevelValue === opt.value}
                              onChange={() =>
                                onAccessLevelChange?.(it.key, opt.value)
                              }
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }

                const isEnabled = !!values?.[it.key];
                const accessLevel = it.accessLevel;
                const accessLevelValue =
                  values?.[accessLevel?.key] ?? accessLevel?.defaultValue;

                return (
                  <div key={it.key} className="feature-config__subcard">
                    <div className="feature-config__toggle-row">
                      <div className="feature-config__toggle-left">
                        <div className="feature-config__toggle-label">
                          {it.label}
                        </div>
                        {it.helper ? (
                          <div className="feature-config__toggle-helper">
                            {it.helper}
                          </div>
                        ) : null}
                      </div>
                      <div className="feature-config__toggle-right">
                        <Form.Check
                          type="switch"
                          id={`feature-${section.id}-${it.key}`}
                          checked={isEnabled}
                          disabled={!!it.disabled}
                          onChange={(e) => onToggle?.(it.key, e.target.checked)}
                        />
                      </div>
                    </div>

                    {isEnabled && accessLevel?.options?.length ? (
                      <div className="feature-config__access-level">
                        <div className="feature-config__access-level-label">
                          {accessLevel.label || "DEFINE DEVICE USER ACCESS LEVEL"}
                        </div>
                        <div className="feature-config__access-level-options">
                          {accessLevel.options.map((opt) => (
                            <Form.Check
                              key={opt.value}
                              inline
                              type="radio"
                              id={`feature-${section.id}-${it.key}-${opt.value}`}
                              name={`feature-access-${section.id}-${it.key}`}
                              label={opt.label}
                              value={opt.value}
                              checked={accessLevelValue === opt.value}
                              onChange={() =>
                                onAccessLevelChange?.(accessLevel.key, opt.value)
                              }
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
};

export default FeatureConfiguration;
